import { Injectable, inject } from '@angular/core';
import { firstValueFrom, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ProductorService } from './productor.service';
import {
  NegocioService,
  ProductorPlanNegocioItem,
  mapProductorNegocioPorIdAPlanNegocioItem,
} from './negocio.service';
import { VisitasService, Visita, VisitasQueryParams, Productor, TipoVisita } from './visitas.service';
import { AuthStore, ID_JERARQUIA_TODAS } from '../../store/auth.store';
import { RutaDiaStorageService, RutaDiaItemLista, RutaDiaSyncMeta } from './ruta-dia-storage.service';

function toYMD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function inicioDiaHace(dias: number): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - dias);
  return d;
}

function horaDesdeIso(fechaIso: string): string {
  try {
    const d = new Date(fechaIso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function nombreProductorDesdeVisita(v: Visita): string {
  const p = v.productor;
  if (!p) return '';
  const parts = [p.nombre, p.primer_apellido, p.segundo_apellido].filter(Boolean);
  return parts.join(' ').trim() || p.nombre || '';
}

/** Máximo permitido por API en query `size` (p. ej. negocio/plan, visitas). */
const TAMANO_PAGINA_API = 100;

/** Concurrencia al pedir planes por id_productor (evita saturar el WebView). */
const CONCURRENCIA_PLANES = 6;

/** Tope de seguridad ante páginas mal reportadas por la API. */
const MAX_PAGINAS_SYNC = 500;

export type RutaDiaSyncProgreso = (mensaje: string) => void;

@Injectable({
  providedIn: 'root'
})
export class RutaDiaSyncService {
  private readonly productorService = inject(ProductorService);
  private readonly negocioService = inject(NegocioService);
  private readonly visitasService = inject(VisitasService);
  private readonly authStore = inject(AuthStore);
  private readonly storage = inject(RutaDiaStorageService);

  /**
   * Descarga y persiste datos para "Ruta del día" (fichas UO, planes de productores en ruta, visitas hoy + historial).
   */
  async ejecutarSincronizacion(onProgreso?: RutaDiaSyncProgreso): Promise<void> {
    const reportar = (mensaje: string) => {
      try {
        onProgreso?.(mensaje);
      } catch {
        /* el overlay de loading no debe tumbar la sync */
      }
    };

    const perfil = this.authStore.obtenerPerfilActivo();
    if (!perfil?.id_perfil) {
      throw new Error('Sin perfil activo');
    }
    const idPerfil = perfil.id_perfil;
    const idJerarquia = ID_JERARQUIA_TODAS;

    reportar('Sincronizando visitas del día...');
    const hoy = toYMD(new Date());
    const visitasHoy = await this.cargarVisitasTodasPaginas(idPerfil, idJerarquia, {
      desde: hoy,
      hasta: hoy,
      sort: 'fecha',
      order: 'asc',
      size: 100
    }, reportar);

    reportar('Sincronizando historial de visitas...');
    const desdeHistorial = toYMD(inicioDiaHace(60));
    const visitasRango = await this.cargarVisitasTodasPaginas(idPerfil, idJerarquia, {
      desde: desdeHistorial,
      hasta: hoy,
      sort: 'fecha',
      order: 'desc',
      size: TAMANO_PAGINA_API
    }, reportar);

    const porProductor = new Map<string, Visita[]>();
    const agregar = (v: Visita) => {
      const id = v.productor?.id_productor;
      if (!id) return;
      const arr = porProductor.get(id) ?? [];
      arr.push(v);
      porProductor.set(id, arr);
    };
    visitasRango.forEach(agregar);

    const visitasHistorialRecortadas: Visita[] = [];
    porProductor.forEach((lista) => {
      const ordenadas = [...lista].sort((a, b) =>
        new Date(b.fecha).getTime() - new Date(a.fecha).getTime()
      );
      visitasHistorialRecortadas.push(...ordenadas.slice(0, 4));
    });

    const mapaIds = new Map<string, Visita>();
    const unir = (v: Visita) => mapaIds.set(v.id_visita, v);
    visitasHoy.forEach(unir);
    visitasHistorialRecortadas.forEach(unir);

    const visitasFinales = [...mapaIds.values()];

    const idsProductoresRuta = new Set<string>();
    for (const v of visitasFinales) {
      const id = String(v.productor?.id_productor ?? '').trim();
      if (id) idsProductoresRuta.add(id);
    }

    reportar('Sincronizando fichas de usuarios INDAP...');
    const productores = await this.cargarTodosLosProductores(idPerfil, idJerarquia, reportar);

    reportar('Sincronizando tipos de visita...');
    const tiposVisita = await this.cargarTiposVisita();

    const planesNegocio = await this.cargarPlanesPorProductores(
      [...idsProductoresRuta],
      reportar
    );

    const lista: RutaDiaItemLista[] = visitasFinales.map(v => ({
      id: v.id_visita,
      idProductor: String(v.productor.id_productor ?? '').trim(),
      nombreProductor: nombreProductorDesdeVisita(v),
      subtituloTipo: v.tipo || '',
      hora: horaDesdeIso(v.fecha),
      lugar: v.lugar || '',
      estado: v.estado || '',
      fechaIso: v.fecha
    }));
    lista.sort((a, b) => new Date(a.fechaIso).getTime() - new Date(b.fechaIso).getTime());

    const meta: RutaDiaSyncMeta = {
      key: 'default',
      lastSyncAt: new Date().toISOString(),
      idPerfil
    };

    reportar('Sincronizando catálogos...');
    const [objetivosVisita, modalidadesContacto, categoriasAnexo] = await Promise.all([
      firstValueFrom(this.visitasService.obtenerObjetivosVisita()),
      firstValueFrom(this.visitasService.obtenerModalidadContacto()),
      firstValueFrom(this.visitasService.obtenerCategoriasAnexo()),
    ]);

    reportar('Guardando datos en el dispositivo...');
    await this.storage.guardarPostSync({
      meta,
      productores,
      tiposVisita,
      planes: planesNegocio,
      visitas: visitasFinales,
      lista,
      objetivosVisita: Array.isArray(objetivosVisita) ? objetivosVisita : [],
      modalidadesContacto: Array.isArray(modalidadesContacto) ? modalidadesContacto : [],
      categoriasAnexo: Array.isArray(categoriasAnexo) ? categoriasAnexo : [],
    });
  }

  private async cargarTodosLosProductores(
    idPerfil: number,
    idJerarquia: number,
    reportar?: RutaDiaSyncProgreso
  ): Promise<Productor[]> {
    const todos: Productor[] = [];
    let page = 1;
    const size = TAMANO_PAGINA_API;
    let pages = 1;

    while (page <= pages && page <= MAX_PAGINAS_SYNC) {
      reportar?.(
        pages > 1
          ? `Sincronizando fichas de usuarios INDAP (${page}/${pages})...`
          : 'Sincronizando fichas de usuarios INDAP...'
      );
      const resp = await firstValueFrom(
        this.productorService.obtenerProductoresPaginados(idPerfil, idJerarquia, undefined, page, size)
      );
      const chunk = resp.items ?? [];
      if (!chunk.length) break;
      todos.push(...chunk);
      pages = Math.max(1, resp.pages || 1);
      if (page >= pages || chunk.length < size) break;
      page++;
    }
    return todos;
  }

  /**
   * Planes / ficha negocio solo para productores presentes en la ruta sincronizada.
   * Evita paginar todo GET negocio/.../jerarquia/-1 (miles de filas).
   */
  private async cargarPlanesPorProductores(
    ids: string[],
    reportar?: RutaDiaSyncProgreso
  ): Promise<ProductorPlanNegocioItem[]> {
    const unicos = [...new Set(ids.map(id => id.trim()).filter(Boolean))];
    if (!unicos.length) {
      reportar?.('Sincronizando planes de negocio...');
      return [];
    }

    const out: ProductorPlanNegocioItem[] = [];
    let completados = 0;
    const total = unicos.length;

    for (let i = 0; i < unicos.length; i += CONCURRENCIA_PLANES) {
      const lote = unicos.slice(i, i + CONCURRENCIA_PLANES);
      reportar?.(`Sincronizando planes de negocio (${Math.min(completados + 1, total)}/${total})...`);

      const resultados = await Promise.all(
        lote.map(id =>
          firstValueFrom(
            this.negocioService.getProductorNegocioPorId(id).pipe(
              map(mapProductorNegocioPorIdAPlanNegocioItem),
              catchError(() => of(null as ProductorPlanNegocioItem | null))
            )
          )
        )
      );

      for (const item of resultados) {
        if (item) out.push(item);
      }
      completados += lote.length;
      reportar?.(`Sincronizando planes de negocio (${completados}/${total})...`);
    }

    return out;
  }

  private async cargarTiposVisita(): Promise<TipoVisita[]> {
    const lista = await firstValueFrom(this.visitasService.obtenerTiposVisita());
    return Array.isArray(lista) ? lista : [];
  }

  private async cargarVisitasTodasPaginas(
    idPerfil: number,
    idJerarquia: number,
    base: VisitasQueryParams,
    reportar?: RutaDiaSyncProgreso
  ): Promise<Visita[]> {
    const out: Visita[] = [];
    let page = 1;
    const size = Math.min(base.size ?? TAMANO_PAGINA_API, TAMANO_PAGINA_API);
    let pages = 1;

    while (page <= pages && page <= MAX_PAGINAS_SYNC) {
      if (pages > 1) {
        reportar?.(`Sincronizando visitas (${page}/${pages})...`);
      }
      const res = await firstValueFrom(
        this.visitasService.obtenerVisitasPorJerarquia(idPerfil, idJerarquia, {
          ...base,
          page,
          size
        })
      );
      out.push(...(res.items ?? []));
      pages = Math.max(1, res.pages || 1);
      if (page >= pages || !res.items?.length) break;
      page++;
    }
    return out;
  }
}
